/**
 * Export Utilities
 * Helper functions for exporting data to various formats
 */

/**
 * Convert array of objects to CSV string
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function arrayToCSV<T extends Record<string, any>>(
  data: T[],
  columns: Array<{ key: keyof T; label: string }>
): string {
  if (data.length === 0) return '';

  // CSV header
  const headers = columns.map((col) => col.label).join(',');

  // CSV rows
  const rows = data.map((item) => {
    return columns
      .map((col) => {
        const value = item[col.key];
        // Escape values containing commas or quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value === null || value === undefined ? '' : String(value);
      })
      .join(',');
  });

  return [headers, ...rows].join('\n');
}

/**
 * Download CSV file
 */
export function downloadCSV(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, filename);
}

/**
 * Download blob as file
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up
  URL.revokeObjectURL(url);
}

/**
 * Format date for filename
 */
export function formatDateForFilename(date: Date = new Date()): string {
  return date
    .toISOString()
    .slice(0, 19)
    .replace(/:/g, '-')
    .replace('T', '_');
}

/**
 * Export data to CSV file
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function exportToCSV<T extends Record<string, any>>(
  data: T[],
  columns: Array<{ key: keyof T; label: string }>,
  baseFilename: string
): void {
  const csv = arrayToCSV(data, columns);
  const timestamp = formatDateForFilename();
  const filename = `${baseFilename}_${timestamp}.csv`;
  downloadCSV(csv, filename);
}

/**
 * Export data from API blob response
 */
export function exportFromBlob(blob: Blob, baseFilename: string, extension: string = 'csv'): void {
  const timestamp = formatDateForFilename();
  const filename = `${baseFilename}_${timestamp}.${extension}`;
  downloadBlob(blob, filename);
}

/**
 * Parse CSV file to JSON
 */
export function parseCSV(csv: string): Array<Record<string, string>> {
  const lines = csv.split('\n');
  if (lines.length === 0) return [];

  const headers = lines[0].split(',');
  const result: Array<Record<string, string>> = [];

  for (let i = 1; i < lines.length; i++) {
    if (!lines[i].trim()) continue;

    const values = lines[i].split(',');
    const obj: Record<string, string> = {};

    headers.forEach((header, index) => {
      obj[header.trim()] = values[index]?.trim() || '';
    });

    result.push(obj);
  }

  return result;
}

/**
 * Read CSV file from FileInput
 */
export function readCSVFile(file: File): Promise<Array<Record<string, string>>> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const csv = e.target?.result as string;
        const data = parseCSV(csv);
        resolve(data);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsText(file);
  });
}
